import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import {
  Container,
  Row,
  Col,
  Nav,
  NavItem,
  NavLink,
  Navbar,
  NavbarBrand,
  Collapse,
} from 'reactstrap';

// Custom CSS import
import 'assets/css/datdriller-landing.css';

const DATDrillerHome = () => {
  const [isVisible, setIsVisible] = useState({});
  const [scrollY, setScrollY] = useState(0);
  const [navbarScrolled, setNavbarScrolled] = useState(false);
  const [navCollapseOpen, setNavCollapseOpen] = useState(false);

  // Intersection Observer for scroll animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible((prev) => ({
              ...prev,
              [entry.target.id]: true,
            }));
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );

    const elements = document.querySelectorAll('.animate-on-scroll');
    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  // Parallax scroll effect and navbar styling
  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
      setNavbarScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const features = [
    {
      icon: 'fas fa-brain',
      title: 'Comprehensive Question Bank',
      description: 'Thousands of DAT-style questions covering all sections with detailed explanations and references.',
      color: '#00B4D8',
    },
    {
      icon: 'fas fa-chart-line',
      title: 'Performance Analytics',
      description: 'Track your progress with detailed analytics across every subject and identify weak areas instantly.',
      color: '#0077B6',
    },
    {
      icon: 'fas fa-clock',
      title: 'Timed Practice Exams',
      description: 'Simulate real DAT conditions with full-length timed practice tests and section-specific drills.',
      color: '#fb6340',
    },
    {
      icon: 'fas fa-mobile-alt',
      title: 'Study Anywhere',
      description: 'Access your question bank on any device - desktop, tablet, or mobile. Study on the go.',
      color: '#11cdef',
    },
    {
      icon: 'fas fa-robot',
      title: 'AI Tutor',
      description: 'Get personalized explanations and guidance powered by AI to help you master each topic.',
      color: '#f5365c',
    },
    {
      icon: 'fas fa-user-md',
      title: 'Expert Authored',
      description: 'Questions written and reviewed by dental professionals and top DAT scorers.',
      color: '#ffd600',
    },
  ];

  const stats = [
    { number: '2,000+', label: 'Practice Questions' },
    { number: '6', label: 'DAT Sections' },
    { number: '100+', label: 'Topics Covered' },
    { number: '24/7', label: 'Access Anywhere' },
  ];

  const testimonials = [
    {
      name: 'Sarah M.',
      role: 'Pre-Dental Student',
      text: 'DATDRILLER was a game-changer for my DAT prep. The questions are incredibly representative of the actual exam, and the detailed explanations helped me understand concepts I had been struggling with for months. I scored a 23 AA!',
      avatar: 'SM',
    },
    {
      name: 'James K.',
      role: 'D1 Dental Student',
      text: 'I used multiple DAT prep resources, but DATDRILLER stood out with its comprehensive question bank and AI tutor feature. Being able to drill questions by specific topic and track my performance made all the difference in my preparation.',
      avatar: 'JK',
    },
    {
      name: 'Priya R.',
      role: 'Pre-Dental Student',
      text: 'The full-length practice exams on DATDRILLER perfectly simulated the real test environment. I loved how the analytics showed me exactly where I needed to improve. The platform is intuitive and the content is top-notch. Highly recommend!',
      avatar: 'PR',
    },
  ];

  const topics = [
    'Biology',
    'General Chemistry',
    'Organic Chemistry',
    'Perceptual Ability (PAT)',
    'Reading Comprehension',
    'Quantitative Reasoning',
    'Anatomy & Physiology',
    'Microbiology',
    'Biochemistry',
    'Cell Biology',
    'Genetics',
    'Ecology & Evolution',
  ];

  return (
    <>
      {/* SEO Meta Tags */}
      <Helmet>
        <title>DATDRILLER | #1 DAT Prep Question Bank for Dental Admission Test</title>
        <meta
          name="description"
          content="Ace your DAT with confidence. Thousands of DAT-style questions, detailed explanations, full-length practice exams, and AI-powered tutoring. Built by dental professionals."
        />
        <meta
          name="keywords"
          content="DAT prep, DAT question bank, dental admission test, DAT practice questions, DAT study guide, DAT review, DAT biology, DAT chemistry, DAT PAT, perceptual ability test, DAT practice exam"
        />
        <meta name="robots" content="index, follow" />
        <meta name="author" content="DATDRILLER" />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content="DATDRILLER | #1 DAT Prep Question Bank" />
        <meta
          property="og:description"
          content="Ace your DAT with confidence. Thousands of DAT-style questions with detailed explanations."
        />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="DATDRILLER | DAT Prep Question Bank" />
        <meta
          name="twitter:description"
          content="Ace your DAT with confidence. Thousands of practice questions."
        />

        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Course',
            name: 'DATDRILLER - DAT Prep Question Bank',
            description: 'The most comprehensive Dental Admission Test prep question bank. Thousands of DAT-style questions with detailed explanations covering all 6 sections.',
            provider: {
              '@type': 'Organization',
              name: 'DATDRILLER',
            },
          })}
        </script>
      </Helmet>

      <div className="dat-landing">
        {/* Navigation */}
        <Navbar className={`dat-navbar navbar-horizontal navbar-dark fixed-top ${navbarScrolled ? 'scrolled' : ''}`} expand="lg">
          <Container>
            <NavbarBrand to="/" tag={Link} className="dat-brand">
              <span className="dat-brand-text">
                <i className="fas fa-tooth mr-2" />
                DATDRILLER
              </span>
            </NavbarBrand>
            <button className="navbar-toggler" onClick={() => setNavCollapseOpen(!navCollapseOpen)}>
              <span className="navbar-toggler-icon" />
            </button>
            <Collapse navbar isOpen={navCollapseOpen}>
              <Nav className="ml-auto align-items-center" navbar>
                <NavItem>
                  <NavLink href="#features" className="dat-nav-link" onClick={() => setNavCollapseOpen(false)}>
                    Features
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink href="#topics" className="dat-nav-link" onClick={() => setNavCollapseOpen(false)}>
                    Topics
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink href="#testimonials" className="dat-nav-link" onClick={() => setNavCollapseOpen(false)}>
                    Testimonials
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink href="#pricing" className="dat-nav-link" onClick={() => setNavCollapseOpen(false)}>
                    Pricing
                  </NavLink>
                </NavItem>
                <NavItem className="ml-lg-3">
                  <Link
                    to="/auth/login"
                    className="btn btn-sm dat-btn-outline"
                    onClick={() => setNavCollapseOpen(false)}
                  >
                    Login
                  </Link>
                </NavItem>
                <NavItem className="ml-lg-2">
                  <Link
                    className="btn btn-sm dat-btn-primary"
                    to="/auth/register"
                    onClick={() => setNavCollapseOpen(false)}
                  >
                    Get Started
                  </Link>
                </NavItem>
              </Nav>
            </Collapse>
          </Container>
        </Navbar>

        {/* Hero Section */}
        <section className="dat-hero">
          <div
            className="dat-hero-bg"
            style={{ transform: `translateY(${scrollY * 0.5}px)` }}
          />
          <div className="dat-hero-overlay" />
          <Container className="dat-hero-content">
            <Row className="align-items-center min-vh-100">
              <Col lg="6" className="text-center text-lg-left">
                <div className="dat-hero-text animate-fade-in-up">
                  <span className="dat-badge">
                    <i className="fas fa-star mr-2" />
                    #1 DAT Prep Platform
                  </span>
                  <h1 className="dat-hero-title">
                    Drill Your Way to a <span className="dat-text-gradient">Top DAT Score</span>
                  </h1>
                  <p className="dat-hero-subtitle">
                    Master the Dental Admission Test with our comprehensive question bank.
                    Thousands of DAT-style questions with detailed explanations written by experts.
                  </p>
                  <div className="dat-hero-cta">
                    <Link
                      className="btn dat-btn-primary dat-btn-lg mr-3"
                      to="/auth/register"
                    >
                      Get Started
                      <i className="fas fa-arrow-right ml-2" />
                    </Link>
                    <a
                      className="btn dat-btn-secondary dat-btn-lg"
                      href="#features"
                    >
                      Learn More
                    </a>
                  </div>
                  <div className="dat-hero-trust mt-4">
                    <span className="dat-trust-text">
                      <i className="fas fa-shield-alt mr-2" />
                      Built by dental professionals & top DAT scorers
                    </span>
                  </div>
                </div>
              </Col>
              <Col lg="6" className="d-none d-lg-block">
                <div className="dat-hero-image animate-fade-in-right">
                  <div className="dat-floating-card dat-card-1 animate-float">
                    <i className="fas fa-check-circle text-success mr-2" />
                    <span>Question Completed!</span>
                  </div>
                  <div className="dat-floating-card dat-card-2 animate-float-delayed">
                    <i className="fas fa-chart-line text-info mr-2" />
                    <span>Score: 24 AA</span>
                  </div>
                  <img
                    src={require('assets/img/brand/tpc-pre-dental-logo-white.png')}
                    alt="DATDRILLER Dashboard"
                    className="dat-hero-logo-main"
                  />
                </div>
              </Col>
            </Row>
          </Container>
          <div className="dat-hero-wave">
            <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M0 120L60 110C120 100 240 80 360 70C480 60 600 60 720 65C840 70 960 80 1080 85C1200 90 1320 90 1380 90L1440 90V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z"
                fill="#ffffff"
              />
            </svg>
          </div>
        </section>

        {/* Stats Section */}
        <section className="dat-stats">
          <Container>
            <Row className="justify-content-center">
              {stats.map((stat, index) => (
                <Col key={index} xs="6" md="3" className="text-center mb-4 mb-md-0">
                  <div
                    id={`stat-${index}`}
                    className={`dat-stat-item animate-on-scroll ${
                      isVisible[`stat-${index}`] ? 'animate-fade-in-up' : 'opacity-0'
                    }`}
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    <p className="dat-stat-number">{stat.number}</p>
                    <p className="dat-stat-label">{stat.label}</p>
                  </div>
                </Col>
              ))}
            </Row>
          </Container>
        </section>

        {/* Features Section */}
        <section id="features" className="dat-features">
          <Container>
            <Row className="justify-content-center mb-5">
              <Col lg="8" className="text-center">
                <h2 className="dat-section-title">
                  Everything You Need to <span className="dat-text-gradient">Ace the DAT</span>
                </h2>
                <p className="dat-section-subtitle">
                  Our comprehensive platform provides all the tools you need for effective DAT preparation.
                </p>
              </Col>
            </Row>
            <Row>
              {features.map((feature, index) => (
                <Col key={index} md="6" lg="4" className="mb-4">
                  <div
                    id={`feature-${index}`}
                    className={`dat-feature-card animate-on-scroll ${
                      isVisible[`feature-${index}`] ? 'animate-fade-in-up' : 'opacity-0'
                    }`}
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    <div
                      className="dat-feature-icon"
                      style={{ backgroundColor: `${feature.color}20`, color: feature.color }}
                    >
                      <i className={feature.icon} />
                    </div>
                    <h3 className="dat-feature-title">{feature.title}</h3>
                    <p className="dat-feature-description">{feature.description}</p>
                  </div>
                </Col>
              ))}
            </Row>
          </Container>
        </section>

        {/* Topics Section */}
        <section id="topics" className="dat-topics">
          <Container>
            <Row className="justify-content-center mb-5">
              <Col lg="8" className="text-center">
                <h2 className="dat-section-title text-white">
                  Comprehensive <span className="dat-text-gradient-light">Topic Coverage</span>
                </h2>
                <p className="dat-section-subtitle text-white-80">
                  Our question bank covers all DAT sections and topics aligned with the ADA DAT content outline.
                </p>
              </Col>
            </Row>
            <Row className="justify-content-center">
              {topics.map((topic, index) => (
                <Col key={index} xs="6" md="4" lg="3" className="mb-3">
                  <div
                    id={`topic-${index}`}
                    className={`dat-topic-tag animate-on-scroll ${
                      isVisible[`topic-${index}`] ? 'animate-fade-in-up' : 'opacity-0'
                    }`}
                    style={{ animationDelay: `${index * 0.05}s` }}
                  >
                    <i className="fas fa-check-circle mr-2" />
                    {topic}
                  </div>
                </Col>
              ))}
            </Row>
          </Container>
        </section>

        {/* How It Works Section */}
        <section className="dat-how-it-works">
          <Container>
            <Row className="justify-content-center mb-5">
              <Col lg="8" className="text-center">
                <h2 className="dat-section-title">
                  How <span className="dat-text-gradient">DATDRILLER</span> Works
                </h2>
                <p className="dat-section-subtitle">
                  Get started in minutes and begin your journey to dental school.
                </p>
              </Col>
            </Row>
            <Row className="align-items-center">
              <Col md="4" className="text-center mb-4 mb-md-0">
                <div
                  id="step-1"
                  className={`dat-step animate-on-scroll ${
                    isVisible['step-1'] ? 'animate-fade-in-up' : 'opacity-0'
                  }`}
                >
                  <div className="dat-step-number">1</div>
                  <h4 className="dat-step-title">Create Account</h4>
                  <p className="dat-step-description">
                    Sign up and choose a plan to get started with your DAT prep.
                  </p>
                </div>
              </Col>
              <Col md="4" className="text-center mb-4 mb-md-0">
                <div
                  id="step-2"
                  className={`dat-step animate-on-scroll ${
                    isVisible['step-2'] ? 'animate-fade-in-up' : 'opacity-0'
                  }`}
                  style={{ animationDelay: '0.2s' }}
                >
                  <div className="dat-step-number">2</div>
                  <h4 className="dat-step-title">Drill Questions</h4>
                  <p className="dat-step-description">
                    Practice with DAT-style questions and learn from detailed explanations.
                  </p>
                </div>
              </Col>
              <Col md="4" className="text-center">
                <div
                  id="step-3"
                  className={`dat-step animate-on-scroll ${
                    isVisible['step-3'] ? 'animate-fade-in-up' : 'opacity-0'
                  }`}
                  style={{ animationDelay: '0.4s' }}
                >
                  <div className="dat-step-number">3</div>
                  <h4 className="dat-step-title">Ace Your DAT</h4>
                  <p className="dat-step-description">
                    Track your progress and walk into your exam with confidence.
                  </p>
                </div>
              </Col>
            </Row>
          </Container>
        </section>

        {/* Testimonials Section */}
        <section id="testimonials" className="dat-testimonials">
          <Container>
            <Row className="justify-content-center mb-5">
              <Col lg="8" className="text-center">
                <h2 className="dat-section-title">
                  What Our <span className="dat-text-gradient">Users Say</span>
                </h2>
                <p className="dat-section-subtitle">
                  Join hundreds of successful pre-dental students who aced their DAT with DATDRILLER.
                </p>
              </Col>
            </Row>
            <Row>
              {testimonials.map((testimonial, index) => (
                <Col key={index} md="4" className="mb-4">
                  <div
                    id={`testimonial-${index}`}
                    className={`dat-testimonial-card animate-on-scroll ${
                      isVisible[`testimonial-${index}`] ? 'animate-fade-in-up' : 'opacity-0'
                    }`}
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    <div className="dat-testimonial-stars">
                      {[...Array(5)].map((_, i) => (
                        <i key={i} className="fas fa-star" />
                      ))}
                    </div>
                    <p className="dat-testimonial-text">"{testimonial.text}"</p>
                    <div className="dat-testimonial-author">
                      <div className="dat-testimonial-avatar">{testimonial.avatar}</div>
                      <div>
                        <h5 className="dat-testimonial-name">{testimonial.name}</h5>
                        <p className="dat-testimonial-role">{testimonial.role}</p>
                      </div>
                    </div>
                  </div>
                </Col>
              ))}
            </Row>
          </Container>
        </section>

        {/* Pricing Section */}
        <section id="pricing" className="dat-pricing">
          <Container>
            <Row className="justify-content-center mb-4">
              <Col lg="8" className="text-center">
                <h2 className="dat-section-title text-white">
                  Simple, <span className="dat-text-gradient-light">Transparent Pricing</span>
                </h2>
                <p className="dat-section-subtitle text-white-80">
                  Choose the plan that works best for your preparation timeline.
                </p>
              </Col>
            </Row>
            <Row className="justify-content-center">
              {/* Pro Plan Yearly */}
              <Col xs="12" md="5" lg="4" className="mb-4">
                <div
                  id="pricing-1"
                  className={`dat-pricing-card dat-pricing-featured animate-on-scroll ${
                    isVisible['pricing-1'] ? 'animate-fade-in-up' : 'opacity-0'
                  }`}
                >
                  <div className="dat-pricing-badge" style={{ fontSize: '0.7rem', padding: '4px 10px' }}>Most Popular</div>
                  <div className="dat-pricing-header">
                    <h3 className="dat-pricing-name">Pro Plan Yearly</h3>
                    <div className="dat-pricing-price">
                      <span className="dat-price-amount">$150</span>
                    </div>
                    <p style={{ color: '#8898aa', fontSize: '0.9rem', marginTop: '5px' }}>$12.50/mo - billed annually</p>
                  </div>
                  <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 1.5rem' }}>
                    <li style={{ padding: '0.5rem 0', color: '#1B2A4A', fontSize: '0.9375rem' }}>
                      <i className="fas fa-check mr-2" style={{ color: '#00B4D8' }}></i>Full Question Bank Access
                    </li>
                    <li style={{ padding: '0.5rem 0', color: '#1B2A4A', fontSize: '0.9375rem' }}>
                      <i className="fas fa-check mr-2" style={{ color: '#00B4D8' }}></i>2,000+ DAT-Style Questions
                    </li>
                    <li style={{ padding: '0.5rem 0', color: '#1B2A4A', fontSize: '0.9375rem' }}>
                      <i className="fas fa-check mr-2" style={{ color: '#00B4D8' }}></i>Detailed Explanations
                    </li>
                    <li style={{ padding: '0.5rem 0', color: '#1B2A4A', fontSize: '0.9375rem' }}>
                      <i className="fas fa-check mr-2" style={{ color: '#00B4D8' }}></i>Performance Analytics
                    </li>
                    <li style={{ padding: '0.5rem 0', color: '#1B2A4A', fontSize: '0.9375rem' }}>
                      <i className="fas fa-check mr-2" style={{ color: '#00B4D8' }}></i>Full-Length Practice Exams
                    </li>
                    <li style={{ padding: '0.5rem 0', color: '#1B2A4A', fontSize: '0.9375rem' }}>
                      <i className="fas fa-check mr-2" style={{ color: '#00B4D8' }}></i>AI Tutor Access
                    </li>
                  </ul>
                  <Link
                    className="btn dat-btn-primary w-100"
                    to="/auth/register"
                  >
                    Get Started
                  </Link>
                </div>
              </Col>
              {/* Lifetime Plan */}
              <Col xs="12" md="5" lg="4" className="mb-4">
                <div
                  id="pricing-2"
                  className={`dat-pricing-card animate-on-scroll ${
                    isVisible['pricing-2'] ? 'animate-fade-in-up' : 'opacity-0'
                  }`}
                  style={{ animationDelay: '0.15s' }}
                >
                  <div className="dat-pricing-header">
                    <h3 className="dat-pricing-name">Lifetime Plan</h3>
                    <div className="dat-pricing-price">
                      <span className="dat-price-amount">$297</span>
                    </div>
                    <p style={{ color: '#8898aa', fontSize: '0.9rem', marginTop: '5px' }}>One-time payment</p>
                  </div>
                  <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 1.5rem' }}>
                    <li style={{ padding: '0.5rem 0', color: '#1B2A4A', fontSize: '0.9375rem' }}>
                      <i className="fas fa-check mr-2" style={{ color: '#00B4D8' }}></i>Everything in Pro Plan
                    </li>
                    <li style={{ padding: '0.5rem 0', color: '#1B2A4A', fontSize: '0.9375rem' }}>
                      <i className="fas fa-check mr-2" style={{ color: '#00B4D8' }}></i>Unlimited Lifetime Access
                    </li>
                    <li style={{ padding: '0.5rem 0', color: '#1B2A4A', fontSize: '0.9375rem' }}>
                      <i className="fas fa-check mr-2" style={{ color: '#00B4D8' }}></i>All Future Updates Included
                    </li>
                    <li style={{ padding: '0.5rem 0', color: '#1B2A4A', fontSize: '0.9375rem' }}>
                      <i className="fas fa-check mr-2" style={{ color: '#00B4D8' }}></i>No Recurring Fees
                    </li>
                  </ul>
                  <Link
                    className="btn dat-btn-outline-dark w-100"
                    to="/auth/register"
                  >
                    Get Started
                  </Link>
                </div>
              </Col>
            </Row>
          </Container>
        </section>

        {/* CTA Section */}
        <section className="dat-cta">
          <Container>
            <Row className="justify-content-center">
              <Col lg="8" className="text-center">
                <h2 className="dat-cta-title">
                  Ready to Ace Your DAT?
                </h2>
                <p className="dat-cta-subtitle">
                  Start preparing for the Dental Admission Test with confidence.
                  Sign up today and begin drilling your way to a top score.
                </p>
                <Link
                  className="btn dat-btn-white dat-btn-lg"
                  to="/auth/register"
                >
                  Get Started
                  <i className="fas fa-arrow-right ml-2" />
                </Link>
              </Col>
            </Row>
          </Container>
        </section>

        {/* Footer */}
        <footer className="dat-footer">
          <Container>
            <Row>
              <Col lg="4" className="mb-4 mb-lg-0">
                <div className="dat-footer-brand-text">
                  <i className="fas fa-tooth mr-2" />
                  DATDRILLER
                </div>
                <p className="dat-footer-description">
                  The most comprehensive DAT prep question bank.
                  Built by dental professionals and top DAT scorers.
                </p>
                <div className="dat-social-links">
                  <a href="mailto:support@datdriller.com" aria-label="Email">
                    <i className="fas fa-envelope" />
                  </a>
                </div>
              </Col>
              <Col lg="2" md="4" className="mb-4 mb-md-0">
                <h5 className="dat-footer-title">Product</h5>
                <ul className="dat-footer-links">
                  <li><a href="#features">Features</a></li>
                  <li><a href="#pricing">Pricing</a></li>
                  <li><a href="#topics">Topics</a></li>
                  <li><a href="#testimonials">Reviews</a></li>
                </ul>
              </Col>
              <Col lg="2" md="4" className="mb-4 mb-md-0">
                <h5 className="dat-footer-title">Resources</h5>
                <ul className="dat-footer-links">
                  <li><Link to="/auth/register">Get Started</Link></li>
                  <li><Link to="/auth/login">Sign In</Link></li>
                </ul>
              </Col>
              <Col lg="2" md="4">
                <h5 className="dat-footer-title">Contact</h5>
                <ul className="dat-footer-links dat-footer-contact">
                  <li>
                    <i className="fas fa-envelope mr-2" />
                    support@datdriller.com
                  </li>
                </ul>
              </Col>
            </Row>
            <hr className="dat-footer-divider" />
            <Row>
              <Col className="text-center">
                <p className="dat-copyright">
                  &copy; {new Date().getFullYear()} DATDRILLER. All rights reserved.
                </p>
              </Col>
            </Row>
          </Container>
        </footer>
      </div>
    </>
  );
};

export default DATDrillerHome;
